import {
  CountryLookup,
  hasShipmentInvoice,
  openAddressLabelPrint,
  openAuthorizationLetterPrint,
  openBoxLabelPrint,
  openProformaInvoicePrint,
  openShipperAccountsCopyPrint,
} from '@/utils/pdfGenerator';
import { Box, Download, FileText, FileStack, Printer, ScrollText, Tag } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { AWBDetail } from '@/services/awbService';
import React from 'react';

interface CountryOption {
  _id: string;
  name: string;
  code?: string;
}

interface AWBDocumentsTabProps {
  awbData: AWBDetail;
  countries?: CountryOption[];
}

interface DocumentOption {
  id: string;
  name: string;
  description: string;
  icon: React.ElementType;
  available: boolean;
  action: () => void;
}

const AWBDocumentsTab: React.FC<AWBDocumentsTabProps> = ({
  awbData,
  countries = [],
}) => {
  // Build countries lookup map
  const countriesMap: CountryLookup = React.useMemo(() => {
    const map: CountryLookup = {};
    countries.forEach(country => {
      if (country._id) {
        map[country._id] = country.name;
      }
    });
    return map;
  }, [countries]);

  // Generate Address Label
  const handleGenerateAddressLabel = () => {
    openAddressLabelPrint(awbData, countriesMap);
  };

  // Generate Box Label
  const handleGenerateBoxLabel = () => {
    openBoxLabelPrint(awbData, countriesMap);
  };

  // Generate Proforma Invoice
  const handleGenerateProformaInvoice = () => {
    openProformaInvoicePrint(awbData, countriesMap);
  };

  // Generate Authorization Letter
  const handleGenerateAuthorizationLetter = () => {
    openAuthorizationLetterPrint(awbData, countriesMap);
  };

  // Generate Shipper & Accounts Copy
  const handleGenerateShipperAccountsCopy = () => {
    openShipperAccountsCopyPrint(awbData, countriesMap);
  };

  // Get box count for description
  const boxCount = awbData.weightDimensions?.length || awbData.totalPieces || 1;

  // Check if shipment invoice is available
  const invoiceAvailable = hasShipmentInvoice(awbData);

  // Document options - only available documents
  const documents: DocumentOption[] = [
    {
      id: 'address-label',
      name: 'Address Label',
      description:
        'Shipper and Consignee address labels with barcode for each parcel',
      icon: Tag,
      available: true,
      action: handleGenerateAddressLabel,
    },
    {
      id: 'box-label',
      name: 'Box Label',
      description: `Full A4 shipping label for each box (${boxCount} ${boxCount === 1 ? 'box' : 'boxes'})`,
      icon: Box,
      available: true,
      action: handleGenerateBoxLabel,
    },
    {
      id: 'proforma-invoice',
      name: 'Proforma Invoice',
      description: invoiceAvailable
        ? `Commercial invoice with ${awbData.shipmentInvoice?.lineItems?.length || 0} line items`
        : 'No shipment invoice data available for this AWB',
      icon: FileText,
      available: invoiceAvailable,
      action: handleGenerateProformaInvoice,
    },
    {
      id: 'authorization-letter',
      name: 'Authorization Letter',
      description: 'Export authorization with KYC document for customs clearance',
      icon: ScrollText,
      available: true,
      action: handleGenerateAuthorizationLetter,
    },
    {
      id: 'shipper-accounts-copy',
      name: 'Shipper & Accounts Copy',
      description: 'Complete 2-page shipment document with terms & conditions',
      icon: FileStack,
      available: true,
      action: handleGenerateShipperAccountsCopy,
    },
  ];

  return (
    <div className='space-y-6'>
      {/* Documents Header */}
      <div className='bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg'>
        <div className='px-4 py-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 rounded-t-lg'>
          <h2 className='text-sm font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2'>
            <Printer className='h-4 w-4' />
            PRINTABLE DOCUMENTS
          </h2>
          <p className='text-xs text-gray-500 dark:text-gray-400 mt-1'>
            Generate and download documents for AWB {awbData.awbNumber}
          </p>
        </div>

        {/* Document Cards Grid */}
        <div className='p-4 grid grid-cols-1 md:grid-cols-2 gap-4'>
          {documents.map(doc => (
            <div
              key={doc.id}
              className={`relative border rounded-lg p-4 transition-all ${
                doc.available
                  ? 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-md cursor-pointer'
                  : 'border-gray-100 dark:border-gray-800 opacity-60'
              }`}
            >
              {/* Coming Soon Badge */}
              {!doc.available && (
                <span className='absolute top-2 right-2 text-xs bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 px-2 py-0.5 rounded-full'>
                  Coming Soon
                </span>
              )}

              <div className='flex items-start gap-4'>
                {/* Icon */}
                <div
                  className={`p-3 rounded-lg ${
                    doc.available
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500'
                  }`}
                >
                  <doc.icon className='h-6 w-6' />
                </div>

                {/* Content */}
                <div className='flex-1 min-w-0'>
                  <h3 className='font-medium text-gray-900 dark:text-gray-100'>
                    {doc.name}
                  </h3>
                  <p className='text-sm text-gray-500 dark:text-gray-400 mt-1'>
                    {doc.description}
                  </p>

                  {/* Action Buttons */}
                  {doc.available && (
                    <div className='mt-3 flex items-center gap-2'>
                      <Button
                        size='sm'
                        variant='outline'
                        onClick={doc.action}
                        className='text-xs'
                      >
                        <Printer className='h-3 w-3 mr-1' />
                        Print / PDF
                      </Button>
                      <Button
                        size='sm'
                        variant='ghost'
                        onClick={doc.action}
                        className='text-xs text-gray-500'
                      >
                        <Download className='h-3 w-3 mr-1' />
                        Preview
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Info */}
      <div className='bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4'>
        <h3 className='text-sm font-medium text-blue-800 dark:text-blue-300 mb-2'>
          💡 How to save as PDF
        </h3>
        <p className='text-sm text-blue-700 dark:text-blue-400'>
          Click on "Print / PDF" button to open a preview. In the print dialog,
          select "Save as PDF" as the destination to download the document as a
          PDF file.
        </p>
      </div>

      {/* AWB Summary for Documents */}
      <div className='bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg'>
        <div className='px-4 py-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 rounded-t-lg'>
          <h2 className='text-sm font-semibold text-gray-800 dark:text-gray-200'>
            DOCUMENT DATA PREVIEW
          </h2>
        </div>
        <div className='p-4'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
            {/* Shipper Info */}
            <div className='space-y-2'>
              <h4 className='font-medium text-gray-700 dark:text-gray-300 text-sm border-b pb-1'>
                Shipper
              </h4>
              <div className='text-sm space-y-1'>
                <p className='font-medium'>
                  {awbData.shipper?.personName ||
                    awbData.shipper?.company ||
                    '-'}
                </p>
                <p className='text-gray-600 dark:text-gray-400'>
                  {[
                    awbData.shipper?.address1,
                    awbData.shipper?.address2,
                    awbData.shipper?.address3,
                  ]
                    .filter(Boolean)
                    .join(', ') || '-'}
                </p>
                <p className='text-gray-600 dark:text-gray-400'>
                  {awbData.shipper?.city}, {awbData.shipper?.stateCounty}
                </p>
                <p className='text-gray-600 dark:text-gray-400'>
                  {awbData.shipper?.postZipCode} -{' '}
                  {typeof awbData.shipper?.country === 'object' &&
                  awbData.shipper?.country
                    ? (awbData.shipper.country as any).name
                    : awbData.shipper?.country}
                </p>
                <p className='text-gray-600 dark:text-gray-400'>
                  📞 {awbData.shipper?.phoneNumber || '-'}
                </p>
              </div>
            </div>

            {/* Consignee Info */}
            <div className='space-y-2'>
              <h4 className='font-medium text-gray-700 dark:text-gray-300 text-sm border-b pb-1'>
                Consignee
              </h4>
              <div className='text-sm space-y-1'>
                <p className='font-medium'>
                  {awbData.consignee?.personName ||
                    awbData.consignee?.company ||
                    '-'}
                </p>
                <p className='text-gray-600 dark:text-gray-400'>
                  {[
                    awbData.consignee?.address1,
                    awbData.consignee?.address2,
                    awbData.consignee?.address3,
                  ]
                    .filter(Boolean)
                    .join(', ') || '-'}
                </p>
                <p className='text-gray-600 dark:text-gray-400'>
                  {awbData.consignee?.city}, {awbData.consignee?.stateCounty}
                </p>
                <p className='text-gray-600 dark:text-gray-400'>
                  {awbData.consignee?.postZipCode} -{' '}
                  {typeof awbData.consignee?.country === 'object' &&
                  awbData.consignee?.country
                    ? (awbData.consignee.country as any).name
                    : awbData.consignee?.country}
                </p>
                <p className='text-gray-600 dark:text-gray-400'>
                  📞 {awbData.consignee?.phoneNumber || '-'}
                </p>
              </div>
            </div>
          </div>

          {/* Parcel Info */}
          <div className='mt-4 pt-4 border-t'>
            <h4 className='font-medium text-gray-700 dark:text-gray-300 text-sm mb-2'>
              Parcels:{' '}
              {awbData.totalPieces || awbData.weightDimensions?.length || 1}
            </h4>
            <p className='text-xs text-gray-500 dark:text-gray-400'>
              Address labels will be generated for each parcel with unique
              parcel numbers.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AWBDocumentsTab;
